import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';

import { useActiveBrand } from '../../components/branding/useActiveBrand';
import { logger } from '../../datadog';
import { setDefaultFaviconAndTitle } from '../../metadata';
import { normalizeError } from '../../utils';
import { AdapterNotInitializedError, useMuteAudio, useStopVideo } from '../../video-sdk';
import { CreateVisitorOptions, createVisitor, visitorAtom } from '../state';
import { renewVisitorUuid } from '../useVisitorId';

export function useResetUserStateCallback() {
  const activeBrand = useActiveBrand();
  const t = useTranslate();
  const setVisitor = useSetAtom(visitorAtom);
  const stopAudio = useMuteAudio();
  const stopVideo = useStopVideo();
  const resetUserState = useCallback(
    async ({ state, name }: CreateVisitorOptions) => {
      setVisitor(createVisitor({ state, name }));
      setDefaultFaviconAndTitle(t, activeBrand);
      // prevents stale admission invites e.g. admitted after timeout
      renewVisitorUuid();

      try {
        await stopAudio();
        await stopVideo();
      } catch (error) {
        if (error instanceof AdapterNotInitializedError) {
          logger.warn('Resetting visitor state before Video SDK adapter initialized');
        } else {
          // unexpected error; log it but don't throw to avoid blocking the state reset
          logger.error('Error stopping audio/video during user state reset', {}, normalizeError(error));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return resetUserState;
}
